import { gradeLesson, type GradeLessonOpts } from "./engine";
import type { BlockRegistry } from "./registry";
import type { AuthoredBlock, BlockResponse, LessonResult } from "./types";

/**
 * Progress anti-fraud. Golden rule: the score that counts is the one the
 * server RECALCULATES with the authoritative record — never the one the
 * client sends. This module re-grades and detects manipulation signals.
 */

export interface AttemptSubmission {
  lessonId: string;
  /** Score the client CLAIMS (ignored except to detect a discrepancy). */
  claimedScore?: number;
  responses: BlockResponse[];
  /** Start and end moment of the attempt (epoch ms). */
  startedAt?: number;
  finishedAt?: number;
}

export interface AntifraudOpts extends GradeLessonOpts {
  /** Plausible minimum ms per answered block (default 400). */
  minMsPerBlock?: number;
  /** Client/server discrepancy tolerance before flagging (default 0.001). */
  scoreTolerance?: number;
  /** IDs of attempts already seen (anti-replay). */
  seenAttemptIds?: ReadonlySet<string>;
  /** ID of this attempt (for anti-replay). */
  attemptId?: string;
}

export type FraudFlag =
  | "score-mismatch" // the client claimed a score different from the recalculated one
  | "too-fast" // answered below the plausible minimum
  | "duplicate-response" // two responses for the same block
  | "unknown-block" // response to a block that doesn't exist in the lesson
  | "replay"; // attempt already processed

export interface AntifraudResult {
  /** AUTHORITATIVE score recalculated on the server. It's the only one that gets persisted. */
  result: LessonResult;
  flags: FraudFlag[];
  /** Accept the attempt? (severe flags ⇒ false). */
  accepted: boolean;
}

const GRAVE: ReadonlySet<FraudFlag> = new Set<FraudFlag>(["replay"]);

/**
 * Verifies and RE-GRADES an attempt. Deterministic; runnable on the server.
 */
export function verifyAttempt(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  submission: AttemptSubmission,
  opts: AntifraudOpts = {},
): AntifraudResult {
  const flags: FraudFlag[] = [];
  const minMs = opts.minMsPerBlock ?? 400;
  const tol = opts.scoreTolerance ?? 0.001;

  // Anti-replay.
  if (opts.attemptId && opts.seenAttemptIds?.has(opts.attemptId)) {
    flags.push("replay");
  }

  // Duplicate responses / to non-existent blocks.
  const blockIds = new Set(blocks.map((b) => b.id));
  const seenResponses = new Set<string>();
  for (const r of submission.responses) {
    if (!blockIds.has(r.blockId)) {
      flags.push("unknown-block");
    }
    if (seenResponses.has(r.blockId)) {
      flags.push("duplicate-response");
    }
    seenResponses.add(r.blockId);
    if (typeof r.elapsedMs === "number" && r.elapsedMs >= 0 && r.elapsedMs < minMs) {
      if (!flags.includes("too-fast")) {
        flags.push("too-fast");
      }
    }
  }

  // Global timing: if the total attempt is faster than minMs × number of blocks.
  if (
    submission.startedAt != null &&
    submission.finishedAt != null &&
    submission.finishedAt > submission.startedAt
  ) {
    const elapsed = submission.finishedAt - submission.startedAt;
    if (elapsed < minMs * blocks.length && !flags.includes("too-fast")) {
      flags.push("too-fast");
    }
  }

  // Authoritative re-grading.
  const result = gradeLesson(registry, blocks, submission.responses, opts);

  // Discrepancy against what the client claimed.
  if (submission.claimedScore != null && Math.abs(submission.claimedScore - result.score) > tol) {
    flags.push("score-mismatch");
  }

  const accepted = !flags.some((f) => GRAVE.has(f));
  return { result, flags, accepted };
}
